
import React from 'react';
import { Comment } from '@/types/social';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Reply, MoreHorizontal, Flag } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import CommentVoteBar from './CommentVoteBar';
import CommentForm from './CommentForm';

interface VisualCommentThreadProps {
  comment: Comment;
  replies: Comment[];
  isRepliesVisible: boolean;
  isRepliesLoading: boolean;
  hasMoreReplies: boolean;
  replyForm: string | null;
  userVotes: Record<string, { up?: boolean; down?: boolean }>;
  upvoteCounts: Record<string, number>;
  downvoteCounts: Record<string, number>;
  maxVotes: number;
  isVoting: Record<string, boolean>;
  isAuthenticated: boolean;
  isSubmitting: boolean;
  onVote: (commentId: string, voteType: 'up' | 'down') => void;
  onToggleReplies: () => void;
  onLoadMoreReplies: () => void;
  onToggleReplyForm: (commentId: string | null) => void;
  onSubmitComment: (content: string, parentId?: string | null) => void;
  onReportComment: (commentId: string, reason: string) => void;
  depth?: number;
}

const VisualCommentThread: React.FC<VisualCommentThreadProps> = ({
  comment,
  replies,
  isRepliesVisible,
  isRepliesLoading,
  hasMoreReplies,
  replyForm,
  userVotes,
  upvoteCounts,
  downvoteCounts,
  maxVotes,
  isVoting,
  isAuthenticated,
  isSubmitting,
  onVote,
  onToggleReplies,
  onLoadMoreReplies,
  onToggleReplyForm,
  onSubmitComment,
  onReportComment,
  depth = 0
}) => {
  const maxDepth = 3;
  const isMaxDepth = depth >= maxDepth;
  const replyCount = comment.reply_count || 0;
  
  // Visual indicators
  const isAuthorHighlighted = comment.profile?.name?.toLowerCase().includes('author');
  const isReported = comment.reported;
  
  const handleReport = (reason: string) => {
    onReportComment(comment.id, reason);
  };

  return (
    <div className={`relative ${depth > 0 ? 'ml-8' : ''}`}>
      {/* Threading line for replies */}
      {depth > 0 && (
        <div className="absolute left-[-20px] top-0 bottom-0 w-0.5 bg-border" />
      )}
      
      {/* Comment content */}
      <div className={`relative bg-background rounded-lg p-4 ${
        depth > 0 ? 'border-l-2 border-muted' : 'border border-border'
      } ${isReported ? 'opacity-60' : ''}`}>
        
        {/* Reported badge */}
        {isReported && (
          <Badge variant="destructive" className="absolute top-2 right-2 text-xs">
            Reported
          </Badge>
        )}
        
        <div className="flex items-start space-x-3">
          <Avatar className="h-8 w-8">
            <AvatarImage src={comment.profile?.avatar_url || ""} />
            <AvatarFallback>
              {comment.profile?.name?.charAt(0).toUpperCase() || 'A'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className={`text-sm font-medium ${
                isAuthorHighlighted ? 'text-primary' : ''
              }`}>
                {comment.profile?.name || 'Anonymous'}
              </div>
              
              {isAuthorHighlighted && (
                <Badge variant="secondary" className="text-xs">
                  Author
                </Badge>
              )}
              
              <div className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
              </div>
              
              {depth > 0 && (
                <Badge variant="outline" className="text-xs">
                  Reply
                </Badge>
              )}
            </div>
            
            <p className="text-sm text-foreground mb-3 break-words">
              {comment.content}
            </p>
            
            <div className="flex items-center gap-4">
              <CommentVoteBar
                commentId={comment.id}
                upvoteCount={upvoteCounts[comment.id] || 0}
                downvoteCount={downvoteCounts[comment.id] || 0}
                maxVotes={maxVotes}
                hasUpvoted={userVotes[comment.id]?.up || false}
                hasDownvoted={userVotes[comment.id]?.down || false}
                isVoting={isVoting[comment.id] || false}
                onVote={onVote}
              />
              
              {!isMaxDepth && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1 h-6"
                  onClick={() => onToggleReplyForm(comment.id)}
                >
                  <Reply className="h-3.5 w-3.5 mr-1" />
                  <span className="text-xs">Reply</span>
                </Button>
              )}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="p-1 h-6">
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleReport('inappropriate')}>
                    <Flag className="h-3.5 w-3.5 mr-2" />
                    Report as inappropriate
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleReport('spam')}>
                    <Flag className="h-3.5 w-3.5 mr-2" />
                    Report as spam
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
      
      {/* Reply form */}
      {replyForm === comment.id && !isMaxDepth && (
        <div className="mt-3 ml-8">
          <CommentForm
            isSubmitting={isSubmitting}
            onSubmit={(content) => onSubmitComment(content, comment.id)}
            isAuthenticated={isAuthenticated}
            placeholder="Write a reply..."
            buttonText="Reply"
            autoFocus={true}
            onCancel={() => onToggleReplyForm(null)}
          />
        </div>
      )}
      
      {/* Reply controls */}
      {replyCount > 0 && (
        <div className="mt-3 ml-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleReplies}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            {isRepliesVisible ? '−' : '+'} {replyCount} {replyCount === 1 ? 'reply' : 'replies'}
          </Button>
        </div>
      )}
      
      {/* Replies */}
      {isRepliesVisible && (
        <div className="mt-3 space-y-3">
          {isRepliesLoading && replies.length === 0 ? (
            <div className="ml-8 text-center py-4">
              <p className="text-sm text-muted-foreground">Loading replies...</p>
            </div>
          ) : (
            <>
              {replies.map((reply) => (
                <VisualCommentThread
                  key={reply.id}
                  comment={reply}
                  replies={[]}
                  isRepliesVisible={false}
                  isRepliesLoading={false}
                  hasMoreReplies={false}
                  replyForm={replyForm}
                  userVotes={userVotes}
                  upvoteCounts={upvoteCounts}
                  downvoteCounts={downvoteCounts}
                  maxVotes={maxVotes}
                  isVoting={isVoting}
                  isAuthenticated={isAuthenticated}
                  isSubmitting={isSubmitting}
                  onVote={onVote}
                  onToggleReplies={() => {}}
                  onLoadMoreReplies={() => {}}
                  onToggleReplyForm={onToggleReplyForm}
                  onSubmitComment={onSubmitComment}
                  onReportComment={onReportComment}
                  depth={depth + 1}
                />
              ))}
              
              {hasMoreReplies && (
                <div className="ml-8">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onLoadMoreReplies}
                    disabled={isRepliesLoading}
                    className="text-xs"
                  >
                    {isRepliesLoading ? 'Loading...' : 'Load more replies'}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VisualCommentThread;
